// The AI participant's spoken voice. Two entry points, both routed through the
// global `voicePlayer` singleton so there is structurally only ever ONE voice:
//
//   - speak(turnId)   manual, from a card's speaker button. Plays now (cutting
//                     whatever's going), no gating — an explicit user action.
//   - announce(turn)  auto, called by the FOCUS MODAL when a salient card pops.
//                     The overlay is the single trigger for auto-speech, so a
//                     spoken contribution is always the one on screen in the
//                     modal — which is what makes it dismissable (close/mute the
//                     modal → stop the audio). It plays only when auto-speak
//                     ("Voice") is on AND the contribution is eligible:
//                       turn.speak === false → never;
//                       turn.speak === true  → always (the AI asked for it);
//                       undefined            → the kind is in the spoken set.
//
// stop(turnId?) cuts the current voice (optionally only if it's that turn) — the
// modal's dismiss/mute and the Voice-off toggle all call it.
//
// Prefs (enabled / which kinds) persist to localStorage and are read LIVE here
// and by the footer/Settings UI, so the controller and the controls never drift.

import { synthesize, type TtsConfig } from '@/lib/ai/tts'
import { voicePlayer } from '@/lib/ai/voicePlayer'
import type { AiTurn, AiTurnKind } from '@/lib/ai/types'
import type { SessionStore } from '@/lib/session/sessionStore'

// --- persisted prefs --------------------------------------------------------

const ENABLED_KEY = 'convariance.autoSpeak.enabled'
const KINDS_KEY = 'convariance.autoSpeak.kinds'

/** Kinds that CAN be voiced (note/insight/candidate are opt-in; present+caution
 *  are the sensible default — the AI's direct contributions and concrete risks). */
export const SPEAKABLE_KINDS: AiTurnKind[] = [
  'present',
  'caution',
  'insight',
  'candidate',
  'note'
]
const DEFAULT_KINDS: AiTurnKind[] = ['present', 'caution']

export function isAutoSpeakEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === '1'
  } catch {
    return false
  }
}

export function setAutoSpeakEnabled(value: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, value ? '1' : '0')
  } catch {
    /* private mode — ignore */
  }
}

export function getSpokenKinds(): AiTurnKind[] {
  try {
    const raw = localStorage.getItem(KINDS_KEY)
    if (!raw) return DEFAULT_KINDS
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return DEFAULT_KINDS
    const kinds = parsed.filter((k): k is AiTurnKind =>
      SPEAKABLE_KINDS.includes(k as AiTurnKind)
    )
    return kinds.length ? kinds : DEFAULT_KINDS
  } catch {
    return DEFAULT_KINDS
  }
}

export function setSpokenKinds(kinds: AiTurnKind[]): void {
  try {
    localStorage.setItem(KINDS_KEY, JSON.stringify(kinds))
  } catch {
    /* private mode — ignore */
  }
}

/** Whether a contribution is eligible to be voiced when its modal pops: the AI's
 *  explicit `speak` flag wins, otherwise the kind-based default. */
export function shouldAutoSpeak(turn: AiTurn): boolean {
  if (turn.speak === false) return false
  if (turn.speak === true) return true
  return getSpokenKinds().includes(turn.kind)
}

// --- controller -------------------------------------------------------------

export interface SpeechController {
  /** Speak a specific turn now (a card's speaker button): cuts whatever's
   *  playing, no eligibility gating — an explicit user action. */
  speak(turnId: string): void
  /** Auto-speak a card the focus modal just surfaced, if Voice is on and the
   *  card is eligible. Always cuts the previous voice first (a newer overlay card
   *  supersedes the old one even when the new one isn't itself spoken). */
  announce(turn: AiTurn): void
  /** Stop the current voice. With `turnId`, only if that turn is the one
   *  playing (so dismissing card A's modal can't cut card B's manual playback). */
  stop(turnId?: string): void
  dispose(): void
}

export function createSpeechController(opts: {
  store: SessionStore
  config: TtsConfig & { outputDeviceId?: string }
}): SpeechController {
  const { store, config } = opts
  const ttsCfg: TtsConfig = {
    apiKey: config.apiKey,
    voiceId: config.voiceId,
    modelId: config.modelId
  }

  let disposed = false

  function turnById(id: string): AiTurn | undefined {
    return store.getSnapshot().aiTurns.find((t) => t.id === id)
  }

  function playTurn(turn: AiTurn): void {
    if (disposed || !turn.text) return
    void voicePlayer.play(
      turn.id,
      () => synthesize(turn.text, ttsCfg).catch(() => null),
      config.outputDeviceId
    )
  }

  return {
    speak(turnId) {
      const turn = turnById(turnId)
      if (turn) playTurn(turn)
    },
    announce(turn) {
      // A new salient overlay card supersedes the previous voice unconditionally.
      voicePlayer.stop()
      if (disposed || !isAutoSpeakEnabled() || !shouldAutoSpeak(turn)) return
      playTurn(turn)
    },
    stop(turnId) {
      if (turnId && voicePlayer.current() !== turnId) return
      voicePlayer.stop()
    },
    dispose() {
      if (disposed) return
      disposed = true
      // Stop any voice this controller started; we're tearing the session down.
      voicePlayer.stop()
    }
  }
}
